import React from 'react';
import { act } from 'react-dom/test-utils';
import { render, screen } from '@testing-library/react';

import useStagedSelection, { ALL_OPTION_ID } from './useStagedSelection';

const OPTIONS = [
  { label: 'Alpha', value: 1 },
  { label: 'Beta', value: 2 },
  { label: 'Gamma', value: 3 },
];

// A probe rather than renderHook: @testing-library/react-hooks is not installed.
let hook;

function Probe({
  allOption = false,
  isOpen = true,
  options = OPTIONS,
  selected = [],
}) {
  hook = useStagedSelection({ allOption, isOpen, options, selected });

  return (
    <div>
      <span data-testid="draft">{hook.draft.join(',')}</span>
      <span data-testid="items">
        {hook.items.map((item) => item.id).join(',')}
      </span>
      <span data-testid="selected">{hook.selectedIds.join(',')}</span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;
const change = (ids) => act(() => hook.handleChange(ids));

afterEach(() => {
  hook = undefined;
});

describe('management/Users useStagedSelection', () => {
  // Applied selections may be numbers (account ids) but OptionsList reports
  // strings, so everything is held as strings.
  it('stringifies the applied selection', () => {
    render(<Probe selected={[1, 2]} />);

    expect(at('draft')).toEqual('1,2');
  });

  it('turns options into OptionsList items', () => {
    render(<Probe />);

    expect(at('items')).toEqual('1,2,3');
  });

  it('stages a change without touching the applied selection', () => {
    render(<Probe selected={[1]} />);

    change(['1', '2']);

    expect(at('draft')).toEqual('1,2');
  });

  // Dismissing a menu must leave what is applied alone, so reopening starts over
  // from it rather than from the abandoned draft.
  it('discards an abandoned draft when it reopens', () => {
    const { rerender } = render(<Probe isOpen selected={[1]} />);

    change(['1', '2']);
    rerender(<Probe isOpen={false} selected={[1]} />);
    rerender(<Probe isOpen selected={[1]} />);

    expect(at('draft')).toEqual('1');
  });

  describe('with a select-all row', () => {
    it('adds the row above the options', () => {
      render(<Probe allOption />);

      expect(at('items')).toEqual(`${ALL_OPTION_ID},1,2,3`);
    });

    it('omits the row when not asked for', () => {
      render(<Probe />);

      expect(at('items')).not.toContain(ALL_OPTION_ID);
    });

    it('takes every option when ticked', () => {
      render(<Probe allOption />);

      change([ALL_OPTION_ID]);

      expect(at('draft')).toEqual('1,2,3');
    });

    it('takes none when unticked', () => {
      render(<Probe allOption selected={[1, 2, 3]} />);

      change(['1', '2', '3']);

      expect(at('draft')).toEqual('');
    });

    // The row is a toggle rather than a value, so it must never reach the caller.
    it('keeps its own id out of the draft', () => {
      render(<Probe allOption />);

      change([ALL_OPTION_ID, '1']);

      expect(at('draft')).not.toContain(ALL_OPTION_ID);
    });

    it('shows itself as checked once every option is', () => {
      render(<Probe allOption selected={[1, 2, 3]} />);

      expect(at('selected')).toEqual(`${ALL_OPTION_ID},1,2,3`);
    });

    it('shows itself unchecked while only some options are', () => {
      render(<Probe allOption selected={[1]} />);

      expect(at('selected')).toEqual('1');
    });

    // Nothing to select all of is not "all selected".
    it('stays unchecked with no options', () => {
      render(<Probe allOption options={[]} />);

      expect(at('selected')).toEqual('');
    });
  });
});
